<template>
  <v-dialog
    v-if="editedEvent"
    :value="dialog"
    persistent
    :retain-focus="false"
    class="p-event-edit-dialog-advanced"
    fullscreen
    @keydown.esc="escape(closeDialog)"
  >
    <v-toolbar
      color="primaryBgr"
      dark
    >
      <p-btn-icon type="close" btn-class="mr-4" tabindex="3" @click="escape(closeDialog)"></p-btn-icon>
      <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn
          :disabled="!isModelChanged()"
          class="d-md-none"
          tabindex="2"
          dark
          text
          @click="submit"
        >
          {{ $t('save') }}
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-card>
      <v-form :disabled="!hasAccess()" ref="form" @submit.stop.prevent>
        <div class="p-dialog-full-screen-container">
          <v-row class="mb-8 mt-0">
            <v-col cols="12" md="7" lg="4">
              <v-text-field
                type="text"
                v-model.trim="editedEvent.title"
                :rules="rules.title"
                :label="$t('event.edit.title')"
                class="p-input-lg"
                tabindex="1"
                clearable
                autofocus
              ></v-text-field>
              <div class="p-event-edit-dialog-advanced"
                   :class="{'mod-allday': editedEvent.allDay}">
                <p-date-time-picker
                  v-model="editedEvent.start"
                  :date-only="editedEvent.allDay"
                  :all-day-clicked="allDayClicked"
                  hide-details="auto"
                  date-class="p-input-solo-filled"
                  time-class="p-input-solo-filled"
                  tabindex="1"
                  dense
                  solo
                  @change="onStartDateChange"
                ></p-date-time-picker>
                <span class="mx-3 pt-1">{{ $t('event.edit.timeTo') }}</span>
                <p-date-time-picker
                  v-model="editedEvent.stop"
                  :date-only="editedEvent.allDay"
                  :all-day-clicked="allDayClicked"
                  hide-details="auto"
                  date-class="p-input-solo-filled"
                  time-class="p-input-solo-filled"
                  tabindex="1"
                  dense
                  solo
                  reversed
                ></p-date-time-picker>
              </div>
              <div
                v-if="dateRangeError"
                v-text="dateRangeError"
                class="v-messages error--text mt-2"
              ></div>

              <v-row class="mt-2 flex-nowrap">
                <v-col cols="auto">
                  <v-checkbox
                    @click="allDayCheckboxClickHandler"
                    v-model="editedEvent.allDay"
                    :label="$t('event.edit.allDay')"
                    class="mt-1"
                    tabindex="1"
                    hide-details
                    dense
                  ></v-checkbox>
                </v-col>
                <v-col cols="auto">
                  <v-select
                    v-model="repeat"
                    :items="repeatOptions"
                    class="p-input-solo-filled"
                    tabindex="1"
                    hide-details
                    solo
                    dense
                    @input="onRepeatChange"
                  ></v-select>
                  <r-rule-dialog
                    :dialog.sync="rRuleDialog"
                    :rrule.sync="editedEvent.rRule"
                    :start-date="startDate"
                  ></r-rule-dialog>
                </v-col>
              </v-row>
              <v-select
                v-model="editedEvent.categoryId"
                :items="eventCategories"
                :rules="rules.categoryId"
                :label="$t('event.edit.category')"
                class="mt-4"
                item-value="id"
                item-text="name"
                tabindex="1"
              ></v-select>
            </v-col>
            <v-col cols="12" md="5" lg="3">
              <v-btn
                v-if="hasAccess()"
                color="primary"
                class="mt-4 hidden-sm-and-down"
                :disabled="!isModelChanged()"
                tabindex="2"
                @click="submit"
              >
                {{ $t('save') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="7" lg="4">
              <label class="p-subtitle-primary mb-5">{{ $t('event.edit.eventDetails') }}</label>
              <p-combobox
                v-model="editedEvent.labels"
                :items="eventLabels"
                :label="$t('event.edit.labels')"
                item-value="id"
                item-text="name"
                prepend-icon="mdi-label-outline"
                class="mb-3"
                tabindex="1"
                clearable
                multiple
                small-chips
                deletable-chips
                hide-selected
              ></p-combobox>
              <p-google-places-autocomplete
                :google-place.sync="editedEvent.googlePlace"
                :google-place-id.sync="editedEvent.googlePlaceId"
                :label="$t('event.edit.location')"
                prepend-icon="mdi-map-marker-outline"
                class="p-input-solo-filled"
                tabindex="1"
                clearable
                solo
                dense
              ></p-google-places-autocomplete>
               <v-textarea
                v-model.trim="editedEvent.note"
                :label="$t('event.edit.note')"
                prepend-icon="mdi-text"
                class="p-input-solo-filled"
                tabindex="1"
                clearable
                solo
              ></v-textarea>
            </v-col>
            <v-col cols="12" md="5" lg="3">
              <label class="p-subtitle-primary mb-4">{{ $t('event.edit.members') }}</label>
              <members-select-list
                v-model="editedEvent.members"
                :label="$t('event.edit.addMember')"
                :disabled="!hasAccess()"
                input-class="p-input-solo-filled"
                tabindex="1"
                clearable
                hide-details
                solo
                dense
              ></members-select-list>
            </v-col>
             <v-col cols="12" md="7" lg="4">
              <div class="p-subtitle-primary mt-n3 mb-4">
                <v-checkbox
                  v-model="editedEvent.isPromoter"
                  :label="$t('event.edit.isPromoter')"
                  class="p-event-edit-dialog-advanced__promoter-checkbox primary--text"
                  tabindex="1"
                  hide-details
                ></v-checkbox>
              </div>
              <div v-if="editedEvent.isPromoter">
                <v-row no-gutters class="mt-0">
                  <v-col cols="6">
                    <v-text-field
                      type="number"
                      v-model.trim="editedEvent.audience"
                      :label="$t('event.edit.audience')"
                      prepend-icon="mdi-account-group-outline"
                      tabindex="1"
                      clearable
                      min="0"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-0">
                  <v-col>
                    <v-text-field
                      type="text"
                      v-model.trim="editedEvent.city"
                      :label="$t('event.edit.city')"
                      prepend-icon="mdi-map-marker-outline"
                      tabindex="1"
                      clearable
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col class="pl-5">
                    <v-text-field
                      type="text"
                      v-model.trim="editedEvent.venueName"
                      :label="$t('event.edit.venue')"
                      tabindex="1"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-0">
                  <v-col>
                    <contact-select-list
                      v-model="editedEvent.promoterId"
                      :label="$t('event.edit.promoter')"
                      :is-promoter-or-copromoter-field="true"
                      prepend-icon="mdi-account-multiple-outline"
                      tabindex="1"
                    ></contact-select-list>
                  </v-col>
                  <v-col class="pl-5">
                    <contact-select-list
                      v-model="editedEvent.coPromoterId"
                      :label="$t('event.edit.coPromoter')"
                      :is-promoter-or-copromoter-field="true"
                      tabindex="1"
                    ></contact-select-list>
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-0">
                  <v-col>
                    <contact-select-list
                      v-model="editedEvent.contactId"
                      :label="$t('event.edit.contact')"
                      css-class="pl-9"
                      tabindex="1"
                    ></contact-select-list>
                  </v-col>
                  <v-col class="pl-5">
                    <contact-select-list
                      v-model="editedEvent.signeeId"
                      :label="$t('event.edit.signee')"
                      tabindex="1"
                    ></contact-select-list>
                  </v-col>
                </v-row>
                <v-row no-gutters class="mt-0">
                  <v-col>
                  <p-number-input
                    v-model="editedEvent.contractSum"
                    :label="$t('event.edit.contractSum')"
                    prepend-icon="mdi-currency-usd"
                    tabindex="1"
                    integer
                  ></p-number-input>
                  </v-col>
                  <v-col class="pl-5">
                    <p-date-time-picker
                      v-model="editedEvent.getIn"
                      :label="$t('event.edit.getIn')"
                      :rules="rules.getIn"
                      hide-details="auto"
                      tabindex="1"
                      clearable
                    ></p-date-time-picker>
                  </v-col>
                </v-row>
                <v-text-field
                  v-model.trim="editedEvent.venueUrl"
                  :label="$t('event.edit.venueUrl')"
                  type="text"
                  prepend-icon="mdi-link"
                  tabindex="1"
                  clearable
                ></v-text-field>
                <v-text-field
                  type="text"
                  v-model.trim="editedEvent.ticketsUrl"
                  :label="$t('event.edit.ticketsUrl')"
                  prepend-icon="mdi-link"
                  tabindex="1"
                  clearable
                ></v-text-field>
                <v-text-field
                  type="text"
                  v-model.trim="editedEvent.ticketPrice"
                  :label="$t('event.edit.ticketPrice')"
                  prepend-icon="mdi-ticket-outline"
                  tabindex="1"
                  clearable
                ></v-text-field>
                <div class="mt-4 pl-9">
                  <div class="subtitle-2">{{ $t('event.edit.venueStatus') }}</div>
                  <v-radio-group
                    v-model="editedEvent.statusId"
                    row
                    dense
                  >
                    <v-radio
                      v-for="status of venueStatuses"
                      :key="status.id"
                      :label="status.name"
                      :value="status.id"
                      tabindex="1"
                    ></v-radio>
                  </v-radio-group>
                </div>
                <div class="mt-4 mb-10 pl-10 d-inline-flex align-center">
                  <v-switch
                    v-model="editedEvent.soldOut"
                    :label="$t('event.edit.soldOut')"
                    inset
                    dense
                    class="p-input-switch"
                    @click.stop
                  ></v-switch>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import { dateTimeFormats } from '@/constants'

import moment from 'moment'
import { RRule } from 'rrule'

import PDateTimePicker from '@/components/PDateTimePicker'
import PGooglePlacesAutocomplete from '@/components/PGooglePlacesAutocomplete'
import PBtnIcon from '@/components/PBtnIcon'
import PNumberInput from '@/components/PNumberInput'
import PCombobox from '@/components/PCombobox'

import RRuleDialog from './RRuleDialog'
import MembersSelectList from './MembersSelectList'
import ContactSelectList from './ContactSelectList'

import venueStatusMixin from '@/mixins/venueStatus.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PDateTimePicker,
    PGooglePlacesAutocomplete,
    PNumberInput,
    PBtnIcon,
    PCombobox,
    RRuleDialog,
    MembersSelectList,
    ContactSelectList
  },


  mixins: [
    venueStatusMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin,
    accessMixin
  ],


  props: {
    event: Object,
    copy: {
      type: Boolean,
      default: false
    }
  },


  data () {
    return {
      rRuleDialog: false,
      repeat: false,
      editedEvent: null,
      defaultEvent: {
        title: '',
        note: null,
        start: null,
        stop: null,
        allDay: false,
        rRule: null,
        categoryId: null,
        googlePlace: null,
        googlePlaceId: null,
        labels: [],
        members: [],
        isPromoter: false,
        city: null,
        venueName: null,
        promoterId: null,
        coPromoterId: null,
        contractSum: null,
        contactId: null,
        signeeId: null,
        getIn: null,
        ticketPrice: null,
        ticketsUrl: null,
        venueUrl: null,
        statusId: 0,
        soldOut: false,
        audience: null
      },
      minAudience: 0,
      allDayClicked: false
    }
  },


  computed: {
    ...mapGetters('eventLabel', ['eventLabels', 'eventLabelById']),
    ...mapGetters('eventCategory', ['eventCategories']),
    ...mapGetters('project', ['currentProjectId']),

    dialogTitle () {
      if (!this.hasAccess()) {
        return this.$t('event.viewEvent')
      }
      if (this.copy) {
        return this.$t('event.copyEvent')
      } else {
        return this.editedEvent.id ? this.$t('event.editEvent') : this.$t('event.createEvent')
      }
    },

    repeatOptions () {
      const repeatText = this.editedEvent?.rRule
        ? RRule.fromString(this.editedEvent.rRule).toText()
        : this.$t('event.repeat')

      return [
        { value: false, text: this.$t('event.noRepeat') },
        { value: true, text: repeatText }
      ]
    },

    startDate () {
      return moment.utc(this.editedEvent.start).format('YYYY-MM-DD')
    },

    dateRangeError () {
      const isValid = moment(this.editedEvent.start).isSameOrBefore(this.editedEvent.stop)
      return isValid ? false : this.$t('validation.eventDateRange')
    },

    rulesToApply () {
      const rule = this.rule

      return {
        title: [rule.required()],
        categoryId: [rule.required()]
      }
    }
  },


  watch: {
    rRuleDialog: function (value) {
      if (value === false) {
        this.repeat = !!this.editedEvent?.rRule
      }
    }
  },


  methods: {
    ...mapActions('event', ['createEvent', 'updateEvent']),
    ...mapActions('changes', ['removeFromChanges']),

    allDayCheckboxClickHandler () {
      this.allDayClicked = true
    },

    onDialogOpen () {
      console.info('onDialogOpen ----------->')
      console.info('this.event === ', this.event)
      if (this.event) {
        this.removeFromChanges(this.event)
      }
      this.allDayClicked = false
      this.initData()
      this.initWatchedModel(this.editedEvent)
      this.resetValidation()
    },

    initData () {
      const event = this.getObjectCopy(this.event || this.defaultEvent)

      if (this.event.audience === null && this.event.promoterId) {
        event.audience = 0
      }

      if (this.copy) {
        delete event.id
      }

      event.start = event.start ?? moment.utc().add(1, 'h').minute(0).second(0).format(dateTimeFormats.dateTimeUtc)
      event.stop = event.stop ?? moment.utc(event.start).add(1, 'h').minute(0).second(0).format(dateTimeFormats.dateTimeUtc)

      this.repeat = !!event.rRule

      if (!event.categoryId && this.eventCategories.length > 0) {
        event.categoryId = this.eventCategories[0].id
      }
      if (event.statusId === null) {
        event.statusId = 0
      }

      this.editedEvent = event
    },

    onRepeatChange () {
      if (this.repeat) {
        this.rRuleDialog = true
      } else {
        this.editedEvent.rRule = null
      }
    },

    onStartDateChange () {
      if (moment.utc(this.editedEvent.stop).isBefore(this.editedEvent.start)) {
        this.editedEvent.stop = moment.utc(this.editedEvent.start).add(1, 'h').format(dateTimeFormats.dateTimeUtc)
      }
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged() || this.dateRangeError) return

      try {
        this.showSnackbarProcessing()

        const event = this.getObjectCopy(this.editedEvent)
        const labels = []
        for (const label of event.labels) {
          if (typeof (label) === 'string') {
            labels.push({ name: label })
          } else {
            labels.push(label)
          }
        }
        event.labels = labels
        event.rRule = this.repeat ? this.editedEvent.rRule : null

        if (event.id) {
          await this.updateEvent(event)
          this.showSnackbarSuccess(this.$t('event.eventUpdated'))
        } else {
          event.projectId = this.currentProjectId
          await this.createEvent(event)
          this.showSnackbarSuccess(this.$t('event.eventCreated'))
        }
        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  }
}
</script>
